//---------------------------------------------------------
// Soft provision API callers (Admin-only endpoints)
//---------------------------------------------------------
import {DELETE_OAM, GET_OAM, POST_OAM, PUT_OAM} from '../fetcher/fetcher_oam';
import type {SoftCatalogFirewall, SoftCatalogImage, SoftCatalogRegion, SoftCatalogType, SoftLinodeAccount, SoftProvisionJob, SoftProvisionJobCreateRequest, SoftSSHKey, SoftTemplate} from 'types/soft_provision';
import {get_local_storage} from 'common';

export async function listLinodeAccounts(): Promise<SoftLinodeAccount[]> {
	const resp = await GET_OAM<{items: SoftLinodeAccount[]}>(`/settings/linode-accounts`);
	if (resp.code !== 200) return [];
	return resp.data?.items ?? [];
}

export async function getCertExpiry(): Promise<{serverDaysLeft: number | null; caDaysLeft: number | null} | null> {
	const resp = await GET_OAM<{serverDaysLeft: number | null; caDaysLeft: number | null}>(`/settings/cert-expiry`);
	if (resp.code !== 200 || !resp.data) return null;
	return {
		serverDaysLeft: resp.data.serverDaysLeft ?? null,
		caDaysLeft: resp.data.caDaysLeft ?? null,
	};
}

export async function createLinodeAccount(
	label: string,
	token: string,
): Promise<{ok: boolean; data?: SoftLinodeAccount; error?: string}> {
	const resp = await POST_OAM<SoftLinodeAccount>(`/settings/linode-accounts`, {label, token});
	if (resp.code !== 201 && resp.code !== 200) return {ok: false, error: resp.message || 'failed to create account'};
	return {ok: true, data: resp.data as SoftLinodeAccount};
}

export async function putLinodeAccount(
	id: string,
	body: {label: string; token?: string},
): Promise<{ok: boolean; data?: SoftLinodeAccount; error?: string}> {
	const resp = await PUT_OAM<SoftLinodeAccount>(`/settings/linode-accounts/${id}`, body);
	if (resp.code !== 200) return {ok: false, error: resp.message || 'failed to update account'};
	return {ok: true, data: resp.data as SoftLinodeAccount};
}

export async function deleteLinodeAccount(id: string): Promise<{ok: boolean; error?: string}> {
	const resp = await DELETE_OAM(`/settings/linode-accounts/${id}`);
	if (resp.code !== 204 && resp.code !== 200) return {ok: false, error: resp.message || 'failed to delete account'};
	return {ok: true};
}

export async function listSSHKeys(): Promise<SoftSSHKey[]> {
	const resp = await GET_OAM<{items: SoftSSHKey[]}>(`/settings/ssh-keys`);
	if (resp.code !== 200) return [];
	return resp.data?.items ?? [];
}

export async function createSSHKey(label: string, publicKey: string): Promise<{ok: boolean; data?: SoftSSHKey; error?: string}> {
	const resp = await POST_OAM<SoftSSHKey>(`/settings/ssh-keys`, {label, publicKey});
	if (resp.code !== 201 && resp.code !== 200) {
		const nested = (resp.data as {error?: {message?: string}} | null)?.error?.message;
		return {ok: false, error: nested || resp.message || 'failed to save key'};
	}
	return {ok: true, data: resp.data as SoftSSHKey};
}

export async function deleteSSHKey(id: string): Promise<{ok: boolean; error?: string}> {
	const resp = await DELETE_OAM(`/settings/ssh-keys/${id}`);
	if (resp.code !== 204 && resp.code !== 200) return {ok: false, error: resp.message || 'failed to delete key'};
	return {ok: true};
}

export async function listSoftTemplates(): Promise<SoftTemplate[]> {
	const resp = await GET_OAM<{items: SoftTemplate[]}>(`/admin/templates`);
	return resp.data?.items ?? [];
}

export async function getSoftTemplate(id: string): Promise<SoftTemplate | undefined> {
	const resp = await GET_OAM<SoftTemplate>(`/admin/templates/${id}`);
	if (resp.code !== 200) return undefined;
	return resp.data as SoftTemplate;
}

export async function createSoftTemplate(body: {
	name: string;
	mode: string;
	osVariants: string[];
	script: string;
	description?: string;
}): Promise<{ok: boolean; data?: SoftTemplate; error?: string}> {
	const resp = await POST_OAM<SoftTemplate>(`/admin/templates`, body);
	if (resp.code !== 201 && resp.code !== 200) return {ok: false, error: resp.message || 'create failed'};
	return {ok: true, data: resp.data as SoftTemplate};
}

export async function updateSoftTemplate(
	id: string,
	body: Partial<{name: string; mode: string; osVariants: string[]; script: string; description: string}>,
): Promise<{ok: boolean; data?: SoftTemplate; error?: string}> {
	const resp = await PUT_OAM<SoftTemplate>(`/admin/templates/${id}`, body);
	if (resp.code !== 200) return {ok: false, error: resp.message || 'update failed'};
	return {ok: true, data: resp.data as SoftTemplate};
}

export async function deleteSoftTemplate(id: string): Promise<{ok: boolean; error?: string}> {
	const resp = await DELETE_OAM(`/admin/templates/${id}`);
	if (resp.code !== 204 && resp.code !== 200) return {ok: false, error: resp.message || 'delete failed'};
	return {ok: true};
}

export async function createProvisionJob(
	body: SoftProvisionJobCreateRequest,
	idempotencyKey?: string,
): Promise<{ok: boolean; data?: SoftProvisionJob; error?: string; code?: string}> {
	const {getApiBaseUrl} = await import('utils/apiProxy');
	const url = `${getApiBaseUrl()}/provision/jobs`;
	const token = get_local_storage('access_token') ?? get_local_storage('token');
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
		...(token ? {Authorization: `Bearer ${token}`} : {}),
		...(idempotencyKey ? {'Idempotency-Key': idempotencyKey} : {}),
	};
	try {
		const r = await fetch(url, {method: 'POST', headers, body: JSON.stringify(body)});
		const data = await r.json().catch(() => ({}));
		if (r.status !== 202 && r.status !== 200) {
			const code = data?.error?.code;
			const msg = data?.error?.message || data?.message || `HTTP ${r.status}`;
			return {ok: false, error: msg, code};
		}
		return {ok: true, data: data as SoftProvisionJob};
	} catch (e: any) {
		return {ok: false, error: e?.message || 'network error'};
	}
}

export async function getProvisionJob(id: string): Promise<SoftProvisionJob | undefined> {
	const resp = await GET_OAM<SoftProvisionJob>(`/provision/jobs/${id}`);
	if (resp.code !== 200) return undefined;
	return resp.data as SoftProvisionJob;
}

export async function listProvisionJobs(limit = 20): Promise<SoftProvisionJob[]> {
	const resp = await GET_OAM<{items: SoftProvisionJob[]}>(`/provision/jobs`, {limit});
	return resp.data?.items ?? [];
}

export async function listLinodeCatalogTypes(accountId: string): Promise<SoftCatalogType[]> {
	const resp = await GET_OAM<{items: SoftCatalogType[]}>(`/catalog/linode/types`, {accountId});
	return resp.data?.items ?? [];
}

export async function listLinodeCatalogRegions(accountId: string): Promise<SoftCatalogRegion[]> {
	const resp = await GET_OAM<{items: SoftCatalogRegion[]}>(`/catalog/linode/regions`, {accountId});
	return resp.data?.items ?? [];
}

export async function listLinodeCatalogImages(accountId: string): Promise<SoftCatalogImage[]> {
	const resp = await GET_OAM<{items: SoftCatalogImage[]}>(`/catalog/linode/images`, {accountId});
	return resp.data?.items ?? [];
}

export async function listLinodeCatalogFirewalls(accountId: string): Promise<SoftCatalogFirewall[]> {
	const resp = await GET_OAM<{items: SoftCatalogFirewall[]}>(`/catalog/linode/firewalls`, {accountId});
	if (resp.code !== 200) throw new Error(resp.message || 'firewall catalog failed');
	return resp.data?.items ?? [];
}

export async function listLinodeCatalogPrivateImages(accountId: string): Promise<SoftCatalogImage[]> {
	const resp = await GET_OAM<{items: SoftCatalogImage[]}>(`/catalog/linode/images`, {accountId, visibility: 'private'});
	if (resp.code !== 200) throw new Error(resp.message || 'private image catalog failed');
	return resp.data?.items ?? [];
}
